import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrentUserResponse } from '@klareospace/pharos-lib-types';
import { ROLE_SCOPE } from '@klareospace/pharos-lib-types';
import { useMasterContext } from '&/master';
import { useSpace } from '../../../settings/spaces/hooks/use-space';
import { useSpaces } from '../../../settings/spaces/hooks/use-spaces';
import { SpaceRow } from './space-row';

// ─── Constants ────────────────────────────────────────────────────────────────

// This panel is a dropdown preview, not a paginated table — its page size is local and fixed.
const SPACES_PANEL_PAGE_SIZE = 10;

// ─── Types ────────────────────────────────────────────────────────────────────

interface DerivedSpace {
  id: string;
  name: string;
  siteCount?: number;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface SpacesPanelProps {
  backendUser: CurrentUserResponse | null;
  canReadSpaces: boolean;
  onBrowseSpace: (spaceId: string, spaceName: string) => void;
  onClose: () => void;
}

// ─── Internal hook result ─────────────────────────────────────────────────────

interface SpacesPanelData {
  displayTotal: number;
  effectiveSiteCountBySpaceId: Record<string, number>;
  hasMore: boolean;
  spaces: DerivedSpace[];
  total: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveScopedSpaces(resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined): DerivedSpace[] {
  if (!resolvedScopes) return [];
  const spaceMap = new Map<string, DerivedSpace>();
  for (const resolvedScope of resolvedScopes) {
    if (resolvedScope.level === ROLE_SCOPE.SPACE) {
      spaceMap.set(resolvedScope.id, { id: resolvedScope.id, name: resolvedScope.name });
    } else if (
      resolvedScope.level === ROLE_SCOPE.SITE &&
      resolvedScope.parentSpaceId &&
      resolvedScope.parentSpaceName
    ) {
      spaceMap.set(resolvedScope.parentSpaceId, {
        id: resolvedScope.parentSpaceId,
        name: resolvedScope.parentSpaceName,
      });
    }
  }
  return Array.from(spaceMap.values());
}

/**
 * Derives site counts per space from resolvedScopes.
 *
 * - Space-level scopes: the backend now populates `siteCount` on each space-level resolved scope.
 * - Site-level scopes: counts are aggregated by parentSpaceId.
 *
 * This works for ALL user types and replaces both `useSiteCountsBySpace` (API users)
 * and the old `deriveSiteCountsBySpace` (scope-derived users).
 */
function deriveSiteCountsFromResolvedScopes(
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): Record<string, number> {
  if (!resolvedScopes) return {};
  const counts: Record<string, number> = {};
  for (const rs of resolvedScopes) {
    if (rs.level === ROLE_SCOPE.SPACE && rs.siteCount != null) {
      counts[rs.id] = rs.siteCount;
    } else if (rs.level === ROLE_SCOPE.SITE && rs.parentSpaceId) {
      counts[rs.parentSpaceId] = (counts[rs.parentSpaceId] ?? 0) + 1;
    }
  }
  return counts;
}

function resolveSiteCountBySpaceId(
  backendUser: CurrentUserResponse | null,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): Record<string, number> {
  // Prefer the backend-provided siteCountsBySpace (covers tenant users who have no space-level resolved scopes),
  // falling back to counts derived from resolvedScopes for backward compatibility.
  return backendUser?.scopeSummary?.siteCountsBySpace ?? deriveSiteCountsFromResolvedScopes(resolvedScopes);
}

// ─── Data hook ────────────────────────────────────────────────────────────────

function useSpacesPanelData(
  backendUser: CurrentUserResponse | null,
  canReadSpaces: boolean,
  currentSpaceId: string | null,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): SpacesPanelData {
  const { data: spacesData } = useSpaces({ enabled: canReadSpaces, page: 1, pageSize: SPACES_PANEL_PAGE_SIZE });
  const { data: currentSpace } = useSpace(currentSpaceId ?? '', canReadSpaces);

  const rawSpaces = spacesData?.items ?? [];
  const total = spacesData?.total ?? 0;

  const apiSpaces: DerivedSpace[] = React.useMemo(() => {
    if (!currentSpaceId || !currentSpace) return rawSpaces;
    const isInList = rawSpaces.some((space) => space.id === currentSpaceId);
    if (isInList) return rawSpaces;
    return [currentSpace, ...rawSpaces.slice(0, SPACES_PANEL_PAGE_SIZE - 1)];
  }, [rawSpaces, currentSpaceId, currentSpace]);

  const scopeDerivedSpaces = React.useMemo(() => deriveScopedSpaces(resolvedScopes), [resolvedScopes]);
  const siteCountBySpaceId = React.useMemo(
    () => resolveSiteCountBySpaceId(backendUser, resolvedScopes),
    [backendUser, resolvedScopes],
  );

  const spaces = canReadSpaces ? apiSpaces : scopeDerivedSpaces;
  const displayTotal = canReadSpaces ? total : spaces.length;

  return {
    displayTotal,
    effectiveSiteCountBySpaceId: siteCountBySpaceId,
    hasMore: total > SPACES_PANEL_PAGE_SIZE,
    spaces,
    total,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SpacesPanel({
  backendUser,
  canReadSpaces,
  onBrowseSpace,
  onClose,
}: SpacesPanelProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { spaceId: currentSpaceId } = useMasterContext();

  const { displayTotal, effectiveSiteCountBySpaceId, hasMore, spaces, total } = useSpacesPanelData(
    backendUser,
    canReadSpaces,
    currentSpaceId,
    backendUser?.resolvedScopes,
  );

  return (
    <div>
      <div className="px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('mainSelector.spaces')} ({displayTotal})
        </span>
      </div>

      <div>
        <div className="p-1">
          {spaces.map((space) => (
            <SpaceRow
              key={space.id}
              canSelectSpace={canReadSpaces}
              onBrowseSpace={onBrowseSpace}
              onClose={onClose}
              siteCount={space.siteCount ?? effectiveSiteCountBySpaceId[space.id] ?? 0}
              space={space}
            />
          ))}
        </div>
      </div>

      {hasMore && (
        <div className="px-3 py-2">
          <span className="text-[11px] text-muted-foreground">
            {t('mainSelector.searchMoreSpaces', { count: total - SPACES_PANEL_PAGE_SIZE })}
          </span>
        </div>
      )}
    </div>
  );
}
